import { useEffect, useRef, useState } from "react";
import CONFIG from "../../config";

import "./MGuideMain.css"

export default function MGuideMain() {
    const MIN_DRAG_START = 15;
    const collapsedWidth = 72;
    const expandedWidth = window.innerWidth * 0.995;

    const [width, setWidth] = useState(collapsedWidth);
    const [transitioning, setTransitioning] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [weekPages, setWeekPages] = useState([]);

    const guideRef = useRef(null);
    const touchStartX = useRef(0);
    const progress = (width - collapsedWidth) / (expandedWidth - collapsedWidth);
    const collapsedOpacity = 1 - progress * 2;
    const expandedOpacity = progress;

    useEffect(() => {
        const fetchWeekGuide = async () => {
            try {
                const res = await fetch(`${CONFIG.apiBase}/tasks/week-guide`);
                if (!res.ok) throw new Error("Failed to fetch week guide");

                const data = await res.json();
                setPages(data);
            } catch (err) {
                console.error("Error fetching week guide:", err);
            }
        };

        if (progress >= 0.95) {
            fetchWeekGuide();
        }
    }, [progress]);

    const setPages = (data) => {
        const today = new Date();
        const year = today.getUTCFullYear();
        const month = today.getUTCMonth();
        const date = today.getUTCDate();

        const timeFormattedTasks = data.map((d) => {
            const formattedTasks = d.tasks.map((t) => {
                const [hours, minutes] = t.time.split(":").map(Number);

                const utcDate = new Date(Date.UTC(year, month, date, hours, minutes));

                const localTime = utcDate.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });

                return {
                    id: t.id,
                    time: localTime,
                    name: t.name,
                    desc: t.desc,
                };
            });

            return { ...d, tasks: formattedTasks };
        });

        setWeekPages(timeFormattedTasks);
    };

    const animateWidth = (targetWidth) => {
        if (targetWidth === collapsedWidth) {
            const el = guideRef.current;
            if (!el) return;

            el.style.width = `${width}px`;
            void el.offsetWidth;
            el.style.transition = "width 0.3s ease";

            requestAnimationFrame(() => {
                el.style.width = `${targetWidth}px`;
            });

            setTimeout(() => setWidth(targetWidth), 100);
        } else {
            setTransitioning(false);

            void guideRef.current?.offsetHeight;

            setTransitioning(true);

            requestAnimationFrame(() => {
                setWidth(targetWidth);
            });

            setTimeout(() => setTransitioning(false), 300);
        }
    };

    const onTabChevron = () => {
        setPageIndex(0)
        animateWidth(width === collapsedWidth ? expandedWidth : collapsedWidth);
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                guideRef.current &&
                !guideRef.current.contains(event.target) &&
                width !== collapsedWidth
            ) {
                animateWidth(collapsedWidth);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [width]);

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        setIsDragging(false);
    };

    const handleTouchMove = (e) => {
        const diff = e.touches[0].clientX - touchStartX.current;

        if (!isDragging && Math.abs(diff) > MIN_DRAG_START) {
            setIsDragging(true);
        }

        if (!isDragging) return;

        if ((pageIndex === 0 && diff > 0) || (pageIndex === weekPages.length - 1 && diff < 0)) {
            setDragX(diff * 0.3);
        } else {
            setDragX(diff);
        }
    };

    const handleTouchEnd = () => {
        const containerWidth = guideRef.current?.offsetWidth ?? window.innerWidth;
        const dragThreshold = containerWidth / 4;

        if (width === collapsedWidth && dragX < -dragThreshold / 4) {
            setIsDragging(false);
            setPageIndex(0)
            animateWidth(expandedWidth);
            setDragX(0);
            return;
        }

        setIsDragging(false);

        if (dragX < -dragThreshold) {
            if (pageIndex === weekPages.length - 1) {
                setDragX(0);
            } else {
                setPageIndex(i => i + 1);
                setDragX(0);
            }
        } else if (dragX > dragThreshold / 4 && pageIndex === 0) {
            animateWidth(collapsedWidth);
            setDragX(0);
        } else if (dragX > dragThreshold) {
            if (pageIndex !== 0) {
                setPageIndex(i => i - 1);
                setDragX(0);
            }
        } else {
            setDragX(0);
        }
    };

    const getTaskStatus = (task, nextTask, taskDate) => {
        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const taskDay = new Date(taskDate);
        taskDay.setHours(0, 0, 0, 0);

        if (taskDay.getTime() !== today.getTime()) return "upcoming";

        const [hours, minutes] = task.time.split(":").map(Number);
        const taskTime = new Date();
        taskTime.setHours(hours, minutes, 0, 0);

        let nextTaskTime;
        if (nextTask) {
            const [nextHours, nextMinutes] = nextTask.time.split(":").map(Number);
            nextTaskTime = new Date();
            nextTaskTime.setHours(nextHours, nextMinutes, 0, 0);
        }

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        if (nextTaskTime) {
            if (now >= taskTime && now < nextTaskTime) return "active";
            if (now >= nextTaskTime) return "inactive";
        } else {
            if (now >= taskTime && now <= endOfDay) return "active";
            if (now > endOfDay) return "inactive";
        }

        return "upcoming";
    };

    const colapsedGuide = () => {
        return (
            <div className="m-colapsed-guide" style={{
                opacity: collapsedOpacity,
                justifyContent: progress < 0.05 ? "end" : "none",
                width: progress < 0.05 ? "100%" : "none",
                display: "flex",
            }}>
                <div className="guide-main-top"></div>
                <div className="chevron-left">
                    <button className="chevron-btn" onClick={onTabChevron}>
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>
                </div>
                <div className="guide-main-bottom"></div>
            </div>
        );
    };

    const expandedGuide = () => {
        return (
            <div className="m-expanded-guide" style={{ opacity: expandedOpacity }}>
                {width < expandedWidth ? ""
                    : <>
                        <div className="chevron-right">
                            <button className="chevron-btn" onClick={onTabChevron}>
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                        <div className="m-expanded-guide-content">
                            {expandedGuideHeader()}
                            {expandedGuideList()}
                        </div>
                    </>}  
            </div>
        );
    };

    const expandedGuideHeader = () => {
        const colorLeftArrow = pageIndex === 0 ? "#666666ff" : "#F5F5F3";
        const colorRightArrow = pageIndex === weekPages.length - 1 ? "#666666ff" : "#F5F5F3";

        return (
            <div className="m-expanded-guide-title">
                <div className="m-text">UPCOMING NEXT WEEK</div>
                <div className="m-date-arrows">
                    <div className="m-left-arrow">
                        <button
                            style={{ color: colorLeftArrow }}
                            onClick={() => { setPageIndex((i) => Math.max(0, i - 1)) }}
                        >
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                    </div>
                    <div className="m-day">{weekPages[pageIndex]?.label}</div>
                    <div className="m-right-arrow">
                        <button
                            style={{ color: colorRightArrow }}
                            onClick={() => {
                                if (pageIndex === weekPages.length - 1) {
                                    console.log("stretch effect here");
                                } else {
                                    setPageIndex((i) => Math.min(weekPages.length - 1, i + 1));
                                }
                            }}
                        >
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const expandedGuideList = () => {
        return (
            <div className="m-expanded-guide-list-main">
                <div
                    className="m-expanded-guide-list"
                    style={{
                        display: "flex",
                        width: `${weekPages.length * 100}%`,
                        transform: `translateX(calc(${-pageIndex * (100 / weekPages.length)}% + ${dragX}px))`,
                        transition: isDragging ? "none" : "transform 0.3s ease",
                    }}
                >
                    {weekPages.map((day, idx) => (
                        <div key={`${day.key}-${idx}`} className="page" style={{ flex: `0 0 calc(100% / ${weekPages.length})` }}>
                            <ul>
                                {day.tasks?.map((task, idx, arr) => {
                                    const nextTask = arr[idx + 1];
                                    const status = getTaskStatus(task, nextTask, weekPages[pageIndex]?.date);

                                    return (
                                        <li key={`${pageIndex}-${task.id}-${idx}`} className={`task-item ${status}`}>
                                            <div className="start-end-time">{task.time}</div>
                                            <div className="task-content">
                                                <div className="task-name">{task.name}</div>
                                                <div className="task-description">{task.desc}</div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div
            ref={guideRef}
            className="guide-main-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
                width: `${width}px`,
                transform: `translateY(0px)`,
                transition: transitioning ? "width 0.3s ease, transform 0.4s ease" : "none",
            }}>
            {progress > 0.95 ? ""
                : colapsedGuide()}
            {progress < 0.95 ? ""
                : expandedGuide()}
        </div>
    );
};